import type { ScanResponse } from "../../types";
import type {
  StudioScanDetailDto,
  StudioScanListItemDto,
} from "../../services/compliance";
import {
  loadStudioScanHistory,
  type StoredStudioScan,
  type StudioVerticalKey,
} from "./studioScanHistoryStorage";

/** 与后端 `review_tasks.id`（UUID）一致 */
const TASK_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isBackendTaskId(id: string): boolean {
  return TASK_UUID_RE.test(id.trim());
}

export function verticalKeyFromProductVertical(pv: string): StudioVerticalKey {
  if (pv === "pet_food") return "pet_food";
  if (pv === "consumer_goods") return "supplements";
  return "cosmetics";
}

export function scanResponseFromResultJson(
  raw: Record<string, unknown> | null | undefined,
  taskId: string,
): ScanResponse {
  if (!raw || typeof raw !== "object") {
    return { score: 0, summary: "", flaggedItems: [], task_id: taskId };
  }
  const flaggedItems = Array.isArray(raw.flaggedItems) ? raw.flaggedItems : [];
  const o: ScanResponse = {
    score: typeof raw.score === "number" ? raw.score : 0,
    summary: typeof raw.summary === "string" ? raw.summary : "",
    flaggedItems: flaggedItems as ScanResponse["flaggedItems"],
    task_id: taskId,
  };
  if (typeof raw.full_rewrite_text === "string") {
    o.full_rewrite_text = raw.full_rewrite_text;
  }
  if (raw.fda && typeof raw.fda === "object") {
    o.fda = raw.fda as ScanResponse["fda"];
  }
  if (typeof raw.credit_balance === "number") {
    o.credit_balance = raw.credit_balance;
  }
  if (typeof raw.is_unlimited === "boolean") {
    o.is_unlimited = raw.is_unlimited;
  }
  return o;
}

export function storedScanFromListItem(item: StudioScanListItemDto): StoredStudioScan {
  const score = item.score ?? 0;
  return {
    scanDisplayId: item.task_id,
    completedAt: new Date(item.created_at).getTime(),
    internalName: item.product_display_name ?? "",
    taskId: item.task_id,
    verticalKey: verticalKeyFromProductVertical(item.product_vertical),
    scanLang: item.scan_lang === "zh" ? "zh" : "en",
    claimsText: item.input_preview,
    result: { score, summary: "", flaggedItems: [] },
    ...(item.series_id ? { seriesId: item.series_id } : {}),
  };
}

export function storedScanFromDetailDto(detail: StudioScanDetailDto): StoredStudioScan {
  const taskId = detail.task_id;
  const result = scanResponseFromResultJson(detail.result_json, taskId);
  return {
    scanDisplayId: taskId,
    completedAt: new Date(detail.created_at).getTime(),
    internalName: detail.product_display_name ?? "",
    taskId,
    verticalKey: verticalKeyFromProductVertical(detail.product_vertical),
    scanLang: detail.scan_lang === "zh" ? "zh" : "en",
    claimsText: detail.input_text,
    result,
    ...(detail.series_id ? { seriesId: detail.series_id } : {}),
  };
}

/** 列表行合并本机定稿时间（仅存在 localStorage 时有效）。 */
export function mergeServerScansWithLocal(
  serverRows: StoredStudioScan[],
): StoredStudioScan[] {
  const local = loadStudioScanHistory();
  const finalizedByTask = new Map<string, number>();
  for (const r of local) {
    if (r.finalizedAt == null) continue;
    finalizedByTask.set(r.scanDisplayId, r.finalizedAt);
    if (r.taskId) finalizedByTask.set(r.taskId, r.finalizedAt);
  }
  return serverRows.map((r) => {
    const fa =
      finalizedByTask.get(r.scanDisplayId) ??
      (r.taskId ? finalizedByTask.get(r.taskId) : undefined);
    return fa != null ? { ...r, finalizedAt: fa } : r;
  });
}
